<template>
    <div class="rich-text-editor" :style="{ border: '1px solid #ccc', zIndex: 100 }">
        <Toolbar
            :style="{ borderBottom: '1px solid #ccc' }"
            :editor="editor"
            :defaultConfig="toolbarConfig"
            :mode="mode"
        />
        <Editor
            :style="{ height: height + 'px', overflowY: 'hidden' }"
            v-model="html"
            :defaultConfig="editorConfig"
            :mode="mode"
            @onCreated="onCreated"
        />
    </div>
</template>

<script>
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { getDefaultToolbarConfig, getDefaultEditorConfig, createToolbarConfig, createEditorConfig } from '@/utils/editorConfig'

export default {
    name: 'RichTextEditor',
    components: {
        Editor,
        Toolbar
    },
    props: {
        // 编辑器内容
        value: {
            type: String,
            default: ''
        },
        // 编辑器高度
        height: {
            type: Number,
            default: 300
        },
        // 占位符文本
        placeholder: {
            type: String,
            default: '请输入内容...'
        },
        // 工具栏类型：'default' | 'simple' | 'basic'
        toolbarType: {
            type: String,
            default: 'default'
        },
        // 自定义工具栏配置
        customToolbarKeys: {
            type: Array,
            default: null
        },
        // 编辑器模式
        mode: {
            type: String,
            default: 'default'
        },
        // 是否禁用
        disabled: {
            type: Boolean,
            default: false
        },
        // 上传配置
        uploadConfig: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            editor: null,
            html: this.value,
            isDestroying: false
        }
    },
    computed: {
        // 工具栏配置
        toolbarConfig() {
            if (this.customToolbarKeys) {
                return createToolbarConfig(this.toolbarType, this.customToolbarKeys)
            }
            return createToolbarConfig(this.toolbarType)
        },
        // 编辑器配置
        editorConfig() {
            const config = {
                placeholder: this.placeholder,
                readOnly: this.disabled,
                ...this.uploadConfig
            }
            return createEditorConfig(config)
        }
    },
    watch: {
        value(newVal) {
            this.html = newVal
        },
        html(newVal) {
            if (!this.isDestroying) {
                this.$emit('input', newVal)
            }
        },
        disabled(newVal) {
            if (this.editor && !this.isDestroying) {
                try {
                    newVal ? this.editor.disable() : this.editor.enable()
                } catch (e) {
                    console.warn('切换富文本编辑器禁用状态失败:', e)
                }
            }
        }
    },
    beforeDestroy() {
        this.isDestroying = true
        const editor = this.editor
        if (editor == null) return
        this.editor = null
        // 必须在 Vue 移除 DOM 之前同步销毁，否则浏览器 selectionchange
        // 会在 DOM 已移除后触发，wangEditor 全局回调找不到实例报错。
        // 先 deselect 清选区 → blur → destroy，全程 try/catch 兜底。
        try {
            if (typeof editor.deselect === 'function') {
                editor.deselect()
            }
        } catch (e) { /* ignore */ }
        try {
            if (typeof editor.blur === 'function') {
                editor.blur()
            }
        } catch (e) { /* ignore */ }
        try {
            if (typeof editor.destroy === 'function' && !editor.isDestroyed) {
                editor.destroy()
            }
        } catch (e) {
            console.warn('富文本编辑器销毁失败:', e)
        }
    },
    methods: {
        // 富文本编辑器创建完成
        onCreated(editor) {
            if (this.isDestroying) {
                try {
                    editor.destroy()
                } catch (e) {
                    console.warn('富文本编辑器创建后立即销毁失败:', e)
                }
                return
            }
            this.editor = editor
            // 粘贴时清除表格 width，防止外部 HTML 的固定像素列宽破坏布局
            editor.on('customPaste', (editorInstance, event, callback) => {
                const clipboardData = event.clipboardData
                if (!clipboardData) { callback(true); return }
                const html = clipboardData.getData('text/html')
                if (!html || !html.includes('<table')) { callback(true); return }
                try {
                    const div = document.createElement('div')
                    div.innerHTML = html
                    div.querySelectorAll('table, colgroup, col, tr, td, th').forEach(el => {
                        el.style.removeProperty('width')
                        el.style.removeProperty('min-width')
                        el.style.removeProperty('max-width')
                        el.removeAttribute('width')
                    })
                    editorInstance.dangerouslyInsertHtml(div.innerHTML)
                    callback(false)
                } catch (e) {
                    console.warn('清理表格宽度失败，使用默认粘贴:', e)
                    callback(true)
                }
            })
            if (this.disabled) {
                this.$nextTick(() => {
                    try {
                        this.editor && this.editor.disable()
                    } catch (e) {
                        console.warn('初始化富文本编辑器禁用状态失败:', e)
                    }
                })
            }
        },
        // 清空内容
        clear() {
            if (this.editor) {
                this.editor.clear()
            }
        },
        // 获取内容
        getContent() {
            return this.html
        },
        // 设置内容
        setContent(content) {
            this.html = content
        },
        // 插入内容
        insertContent(content) {
            if (this.editor) {
                this.editor.insertText(content)
            }
        },
        // 获取纯文本内容
        getText() {
            if (this.editor) {
                return this.editor.getText()
            }
            return ''
        }
    }
}
</script>

<style lang="less" scoped>
.rich-text-editor {
    border-radius: 4px;
    
    :deep(.w-e-text-container) {
        border-radius: 0 0 4px 4px;
    }
    
    :deep(.w-e-toolbar) {
        border-radius: 4px 4px 0 0;
    }
}
</style>

<style>
/* 全局 CSS Reset（* { margin:0; padding:0 }）会清掉 wangEditor 内容区
   里所有 HTML 标签的默认间距，导致排版混乱。此处在非 scoped 区域还原。 */
.w-e-text-container [data-slate-editor] p {
    margin: 0.75em 0;
}
.w-e-text-container [data-slate-editor] ul,
.w-e-text-container [data-slate-editor] ol {
    margin: 0.5em 0;
    padding-left: 1.8em;
}
.w-e-text-container [data-slate-editor] li {
    margin: 0.25em 0;
}
.w-e-text-container [data-slate-editor] h1,
.w-e-text-container [data-slate-editor] h2,
.w-e-text-container [data-slate-editor] h3,
.w-e-text-container [data-slate-editor] h4,
.w-e-text-container [data-slate-editor] h5,
.w-e-text-container [data-slate-editor] h6 {
    margin: 0.67em 0;
}
.w-e-text-container [data-slate-editor] blockquote {
    margin: 0.5em 0;
    padding: 0.25em 1em;
}
.w-e-text-container [data-slate-editor] pre {
    margin: 0.5em 0;
    padding: 0.75em 1em;
}
/* ── 表格：选择器不依赖 [data-slate-editor]，兼容各版本 wangEditor ── */
.w-e-text-container table {
    border-collapse: collapse;
    width: 100% !important;
    table-layout: auto !important;
}
.w-e-text-container table col,
.w-e-text-container table colgroup col {
    width: auto !important;
}
.w-e-text-container table td,
.w-e-text-container table th {
    border: 1px solid #ccc;
    padding: 4px 8px;
    box-sizing: border-box;
    vertical-align: middle;
    width: auto !important;
    min-width: 40px;
    word-break: break-word;
}
</style>
